import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { catchError, of, timer } from 'rxjs';

import { map, switchMap } from 'rxjs/operators';

import { FinEnvironmentService } from '@finverity/platform-app/core/services/environment.service';

import { ServiceName } from '../enums/service-name.enum';
import { DependencyStatus, ServiceHealth } from '../models/service.model';
import { createHealthCheckUrl } from '../utils/url-builder.util';

import { API_ENDPOINTS } from './api-endpoints';

interface HealthCheck {
  status: 'OK' | 'ERROR' | 'LOADING';
  components?: DependencyStatus[];
  scheduledMaintenance?: any;
}

@Injectable({
  providedIn: 'root',
})
export class ApiStatusService {
  private readonly environmentService = inject(FinEnvironmentService);
  private readonly http = inject(HttpClient);

  private readonly gatewayUrl = this.environmentService.api.gatewayUrl;
  private readonly ssoUrl = this.environmentService.api.SSOUrl;

  getDefaultServices(): ServiceHealth[] {
    return API_ENDPOINTS.map(endpoint => ({
      name: endpoint.name,
      description: endpoint.description,
      status: 'LOADING',
      dependencies: [],
    }));
  }

  fetchGraphQLStatuses() {
    return timer(0, 60000).pipe(
      switchMap(() =>
        this.http.get<Record<string, HealthCheck>>(createHealthCheckUrl(ServiceName.Gateway, this.gatewayUrl))
      ),
      map(response => this.parseServiceResponse(response)),
      catchError(error => {
        console.error('GraphQL health check request error:', error);
        return of([]);
      })
    );
  }

  fetchSSOStatus() {
    return timer(0, 60000).pipe(
      switchMap(() => this.http.get<HealthCheck>(`${this.ssoUrl}/health-check`)),
      map(response => this.parseSSOResponse(response)),
      catchError(error => {
        console.error('SSO health check request error:', error);
        return of([]);
      })
    );
  }

  private parseServiceResponse(response: Record<string, HealthCheck>): ServiceHealth[] {
    return Object.entries(response).map(([name, data]) => {
      const endpoint = API_ENDPOINTS.find(endpoint => endpoint.serviceName === name);
      return {
        name: endpoint?.name || name,
        description: endpoint?.description || '',
        status: data?.status ?? 'ERROR',
        dependencies: (data?.components ?? []).map((component: DependencyStatus) => ({
          name: component.name,
          status: component.status ?? 'DOWN',
        })),
      };
    });
  }

  private parseSSOResponse(response: { status: string; components?: DependencyStatus[] }): ServiceHealth {
    const validStatuses: ServiceHealth['status'][] = ['OK', 'ERROR', 'LOADING'];
    const endpoint = API_ENDPOINTS.find(endpoint => endpoint.serviceName === 'sso');

    return {
      name: endpoint?.name || 'sso',
      description: endpoint?.description || '',
      status: validStatuses.includes(response?.status as any) ? (response?.status as ServiceHealth['status']) : 'ERROR',
      dependencies: (response?.components ?? []).map((component: DependencyStatus) => ({
        name: component.name,
        status: component.status ?? 'DOWN',
      })),
    };
  }
}
